/**
 * Fog of war (R18, R19, KTD7).
 *
 * **Vision lives in the simulation; the renderer only draws it.** Fog that is
 * only a rendering effect is a bug factory — units shoot what the player cannot
 * see, and the AI cheats without anyone deciding it should. Keeping the grids
 * in the simulation also puts them inside the state hash, so a fog divergence
 * surfaces as a named desync rather than as a mysterious inconsistency.
 *
 * Two layers per player:
 *  - `explored` is monotonic within a match. Terrain and enemy *structures*
 *    seen once stay known.
 *  - `visible` is recomputed incrementally as units move. Enemy *units* are
 *    hidden the moment their cell leaves it, which is the correct Red Alert
 *    behaviour: enemy positions are not persistent knowledge.
 *
 * Standing on high ground sees further, which together with the reach bonus in
 * `combat.ts` is what makes a plateau worth taking.
 *
 * Cliffs still do **not** block line of sight, and that remains a decision
 * rather than an oversight — `vision.test.ts` asserts it so the deferred
 * question stays recorded. Blocking would mean tracing a ray per cell of every
 * stamp, and worse, it would make a stamp depend on terrain that can change
 * under it: `unstamp` reproduces the set it laid down from the remembered cell
 * and radius, so if the terrain moved in between the counts would leak and the
 * fog would slowly fill in with cells nobody can see.
 */

import type { Ctx, Subsystem } from '../core/registry.js'
import { MAP_SIZE, MAX_ENTITIES, MAX_PLAYERS } from '../core/config.js'
import { Entities, EF } from './entities.js'
import { World } from './world.js'
import { defById } from './data/entities.js'
import * as fix from '../core/fixed.js'

const CELLS = MAP_SIZE * MAP_SIZE
const NO_CELL = -1

/** Extra sight radius per level of ground under a unit, and its cap. */
const MAX_VISION_BONUS = 2

/**
 * The satellite sweep.
 *
 * A survey structure used to reveal enemy *structures* permanently and nothing
 * else, which is defensible but is not what a satellite feels like — you still
 * cannot see an army massing, and the map still reads as mostly shroud in the
 * late game when both bases are built out and the interesting question is
 * where everybody's units are.
 *
 * A permanent live reveal is the other extreme and it is not survivable: one
 * bought at the tech lab moved the faction win rate from 52/48 to 65/35 across
 * 180 bot matches, because perfect information is close to unanswerable.
 *
 * A sweep is the version worth having. Everything is visible for a few
 * seconds, then the shroud closes again until the next pass. You get the
 * overview — where their army is, what they have been building — as a series
 * of snapshots, and scouting still matters in between, because a snapshot ages.
 * Hiding remains possible; hiding *permanently* does not.
 */
export const SWEEP_PERIOD_TICKS = 30 * 45
export const SWEEP_DURATION_TICKS = 30 * 5

/** Precomputed circular stamps, indexed by radius. */
const stampCache = new Map<number, Int32Array>()

function stampFor(radius: number): Int32Array {
  const cached = stampCache.get(radius)
  if (cached) return cached
  const offsets: number[] = []
  const r2 = radius * radius
  for (let dy = -radius; dy <= radius; dy++) {
    for (let dx = -radius; dx <= radius; dx++) {
      if (dx * dx + dy * dy > r2) continue
      offsets.push(dx, dy)
    }
  }
  const out = Int32Array.from(offsets)
  stampCache.set(radius, out)
  return out
}

export class Vision implements Subsystem {
  static id = 'vision'
  static deps = ['world', 'entities'] as const

  private entities!: Entities
  private world!: World

  /** Per player: how many entities currently see each cell. */
  readonly counts: Uint16Array[] = []
  /** Per player: cells ever seen. Monotonic within a match. */
  readonly explored: Uint8Array[] = []

  /** Where each entity last stamped from, so the stamp can be withdrawn. */
  private readonly lastCell = new Int32Array(MAX_ENTITIES).fill(NO_CELL)
  private readonly lastRadius = new Int32Array(MAX_ENTITIES)
  private readonly lastOwner = new Int8Array(MAX_ENTITIES).fill(-1)

  /**
   * Last-known enemy structures, per player, as `cell -> kind`. Enemy
   * structures persist in explored territory; enemy units do not.
   */
  /** Whether each player has ever completed a survey structure. */
  private readonly surveyed = new Uint8Array(MAX_PLAYERS)
  /**
   * Tick each player's current sweep ends on, or 0 for "not sweeping".
   *
   * Simulation state, so it is hashed: a client that thought a sweep was
   * running when its peer did not would acquire targets the peer could not
   * see, and the divergence would surface as combat rather than as fog.
   */
  private readonly sweepUntil = new Int32Array(MAX_PLAYERS)
  /** The tick being simulated, for the sweep schedule. */
  private tick = 0
  private readonly remembered: Map<number, number>[] = []
  private rememberedHashBuf = new Int32Array(0)

  constructor(readonly playerCount = MAX_PLAYERS) {
    for (let p = 0; p < playerCount; p++) {
      this.counts.push(new Uint16Array(CELLS))
      this.explored.push(new Uint8Array(CELLS))
      this.remembered.push(new Map())
    }
  }

  private ctx!: Ctx

  init(ctx: Ctx): void {
    this.ctx = ctx
    this.entities = ctx.get<Entities>('entities')
    this.world = ctx.get<World>('world')

    for (let p = 0; p < this.playerCount; p++) {
      ctx.hash.register(`vision:visible:${p}`, this.counts[p])
      ctx.hash.register(`vision:explored:${p}`, this.explored[p])
    }
    ctx.hash.register('vision:remembered', () => this.rememberedView())
    ctx.hash.register('vision:surveyed', this.surveyed)
    ctx.hash.register('vision:sweep', this.sweepUntil)

    ctx.on('actor:removed', (p) => this.unstamp((p as { slot: number }).slot))
  }

  fixedUpdate(tick: number): void {
    this.tick = tick
    const limit = this.entities.limit
    for (let slot = 0; slot < limit; slot++) {
      if (!this.entities.isAlive(slot)) {
        if (this.lastCell[slot] !== NO_CELL) this.unstamp(slot)
        continue
      }
      const owner = this.entities.owner[slot]
      if (owner < 0 || owner >= this.playerCount) continue

      const cell = this.world.cellAt(
        fix.raw(this.entities.x[slot]),
        fix.raw(this.entities.y[slot]),
      )
      if (cell < 0) continue
      const def = defById(this.entities.kind[slot])
      // Height is read from the cell rather than from the unit, so a tank that
      // drives up a ramp gains the same view as one built on the plateau.
      const radius = (def ? def.vision : 4) + this.heightBonus(cell)

      if (this.lastCell[slot] === cell && this.lastRadius[slot] === radius) continue
      this.unstamp(slot)
      this.stamp(slot, owner, cell, radius)
    }

    this.rememberStructures()
    this.applySurvey()
  }

  /**
   * A survey structure lifts the shroud, and keeps the lights on while it lives.
   *
   * The two layers are treated differently on purpose. `explored` is monotonic
   * — once you have seen the terrain you know it, and losing the building does
   * not give you amnesia. Live visibility is *not*: it lasts exactly as long as
   * the structure providing it stands, is finished, and has power.
   *
   * That distinction is the whole balance of the thing. A permanent, live,
   * whole-map reveal bought once at the tech lab is close to unanswerable, and
   * the bot-match sweep said so plainly: giving both factions one moved the
   * faction win rate from 52/48 to 65/35 across 180 matches. Making it depend
   * on a building that can be destroyed — or simply switched off by cutting
   * power — turns an unconditional advantage into a target.
   */
  private applySurvey(): void {
    const live = new Uint8Array(this.playerCount)
    const limit = this.entities.limit
    for (let slot = 0; slot < limit; slot++) {
      if (!this.entities.isAlive(slot)) continue
      if (this.entities.has(slot, EF.Building)) continue
      if (this.entities.has(slot, EF.Offline)) continue
      const owner = this.entities.owner[slot]
      if (owner < 0 || owner >= this.playerCount) continue
      if (!defById(this.entities.kind[slot])?.revealsMap) continue
      live[owner] = 1
    }

    for (let owner = 0; owner < this.playerCount; owner++) {
      if (live[owner] && !this.surveyed[owner]) {
        this.explored[owner].fill(1)
        this.ctx.emit('vision:survey', { owner })
      }
      this.surveyed[owner] = live[owner]

      if (!live[owner]) {
        this.sweepUntil[owner] = 0
        continue
      }
      // Scheduled off the tick rather than off a countdown so that losing the
      // building and rebuilding it cannot be used to re-trigger a sweep on
      // demand. The pass comes round when it comes round.
      if (this.tick % SWEEP_PERIOD_TICKS === 0) {
        this.sweepUntil[owner] = this.tick + SWEEP_DURATION_TICKS
        this.explored[owner].fill(1)
        this.ctx.emit('vision:sweep', { owner })
      }
    }
  }

  /** Whether this player's satellite is mid-pass. */
  isSweeping(player: number): boolean {
    return this.sweepUntil[player] > this.tick
  }

  /**
   * Extra sight for standing high up.
   *
   * Absolute rather than relative, unlike the reach bonus in combat: you can
   * see further from a hill regardless of what is below you, and there is
   * nothing to compare against when the question is "what can this unit see".
   */
  private heightBonus(cell: number): number {
    return Math.min(MAX_VISION_BONUS, this.world.height[cell])
  }

  private stamp(slot: number, owner: number, cell: number, radius: number): void {
    const counts = this.counts[owner]
    const explored = this.explored[owner]
    const cx = cell % MAP_SIZE
    const cy = (cell / MAP_SIZE) | 0
    const offsets = stampFor(radius)
    for (let i = 0; i < offsets.length; i += 2) {
      const x = cx + offsets[i]
      const y = cy + offsets[i + 1]
      if (x < 0 || y < 0 || x >= MAP_SIZE || y >= MAP_SIZE) continue
      const idx = y * MAP_SIZE + x
      counts[idx]++
      explored[idx] = 1
    }
    this.lastCell[slot] = cell
    this.lastRadius[slot] = radius
    this.lastOwner[slot] = owner
  }

  private unstamp(slot: number): void {
    const cell = this.lastCell[slot]
    if (cell === NO_CELL) return
    const owner = this.lastOwner[slot]
    this.lastCell[slot] = NO_CELL
    if (owner < 0 || owner >= this.playerCount) return

    const counts = this.counts[owner]
    const cx = cell % MAP_SIZE
    const cy = (cell / MAP_SIZE) | 0
    const offsets = stampFor(this.lastRadius[slot])
    for (let i = 0; i < offsets.length; i += 2) {
      const x = cx + offsets[i]
      const y = cy + offsets[i + 1]
      if (x < 0 || y < 0 || x >= MAP_SIZE || y >= MAP_SIZE) continue
      const idx = y * MAP_SIZE + x
      if (counts[idx] > 0) counts[idx]--
    }
    this.lastOwner[slot] = -1
  }

  /** Record enemy structures seen this tick as last-known state. */
  private rememberStructures(): void {
    const limit = this.entities.limit
    for (let slot = 0; slot < limit; slot++) {
      if (!this.entities.isAlive(slot)) continue
      if (!this.entities.has(slot, EF.Structure)) continue
      const cell = this.world.cellAt(
        fix.raw(this.entities.x[slot]),
        fix.raw(this.entities.y[slot]),
      )
      if (cell < 0) continue
      const owner = this.entities.owner[slot]
      for (let p = 0; p < this.playerCount; p++) {
        if (p === owner) continue
        if (this.counts[p][cell] > 0) this.remembered[p].set(cell, this.entities.kind[slot])
      }
    }
  }

  // --- queries -------------------------------------------------------------

  isVisible(player: number, cell: number): boolean {
    if (player < 0 || player >= this.playerCount || cell < 0) return false
    if (this.isSweeping(player)) return true
    return this.counts[player][cell] > 0
  }

  isExplored(player: number, cell: number): boolean {
    if (player < 0 || player >= this.playerCount || cell < 0) return false
    return this.explored[player][cell] !== 0
  }

  isVisibleAt(player: number, cx: number, cy: number): boolean {
    if (cx < 0 || cy < 0 || cx >= MAP_SIZE || cy >= MAP_SIZE) return false
    return this.isVisible(player, cy * MAP_SIZE + cx)
  }

  /**
   * Can `player` currently see entity `slot`?
   *
   * This is the query targeting and AI perception go through (R19). Building
   * combat against raw positions and retrofitting fog afterwards produces
   * exactly the "unit shoots through fog" class the ordering exists to prevent.
   */
  canSee(player: number, slot: number): boolean {
    if (!this.entities.isAlive(slot)) return false
    if (this.entities.owner[slot] === player) return true
    // A survey shows you the map and everything built on it — but not armies.
    //
    // The first version of this revealed *everything* live, and 180 bot matches
    // put the faction win rate at 63/37 against a 52/48 baseline: perfect
    // information is close to unanswerable, and handing it to one side for the
    // price of a mid-tier building decided matches on its own.
    //
    // Structures only is the version worth having. It answers the question a
    // satellite should answer — where is their base, what have they built — and
    // leaves the one scouting exists for: where is their army right now.
    if (this.isSweeping(player)) return true
    if (this.surveyed[player] && this.entities.has(slot, EF.Structure)) return true
    const cell = this.world.cellAt(fix.raw(this.entities.x[slot]), fix.raw(this.entities.y[slot]))
    return this.isVisible(player, cell)
  }

  /** Last-known enemy structure kind at a cell, or 0. */
  rememberedAt(player: number, cell: number): number {
    return this.remembered[player]?.get(cell) ?? 0
  }

  /**
   * Full recomputation. Used only by the test that asserts the incremental
   * path has not drifted — if these ever disagree, the incremental update is
   * the bug.
   */
  recomputeAll(): void {
    for (let p = 0; p < this.playerCount; p++) this.counts[p].fill(0)
    this.lastCell.fill(NO_CELL)
    this.lastOwner.fill(-1)
    const limit = this.entities.limit
    for (let slot = 0; slot < limit; slot++) {
      if (!this.entities.isAlive(slot)) continue
      const owner = this.entities.owner[slot]
      if (owner < 0 || owner >= this.playerCount) continue
      const cell = this.world.cellAt(fix.raw(this.entities.x[slot]), fix.raw(this.entities.y[slot]))
      if (cell < 0) continue
      const def = defById(this.entities.kind[slot])
      // The same radius the incremental path would have used. Leaving the
      // height bonus out here is not a cosmetic difference: `recomputeAll` is
      // the reference the incremental update is checked against, so the two
      // disagreeing means one of them is wrong and the test cannot say which.
      this.stamp(slot, owner, cell, (def ? def.vision : 4) + this.heightBonus(cell))
    }
  }

  /** Flattened last-known structures, ascending by cell, for hashing. */
  private rememberedView(): Int32Array {
    let need = 0
    for (const m of this.remembered) need += m.size * 2 + 1
    if (this.rememberedHashBuf.length < need) this.rememberedHashBuf = new Int32Array(need * 2)

    let o = 0
    for (let p = 0; p < this.playerCount; p++) {
      const keys: number[] = []
      for (const k of this.remembered[p].keys()) keys.push(k)
      keys.sort((a, b) => a - b)
      this.rememberedHashBuf[o++] = keys.length
      for (const k of keys) {
        this.rememberedHashBuf[o++] = k
        this.rememberedHashBuf[o++] = this.remembered[p].get(k)!
      }
    }
    return this.rememberedHashBuf.subarray(0, o)
  }

  /**
   * Pack the two layers into one byte per cell for the renderer's fog texture:
   * 0 unexplored, 1 explored-not-visible, 2 visible.
   */
  packForRender(player: number, out: Uint8Array): Uint8Array {
    const counts = this.counts[player]
    const explored = this.explored[player]
    for (let i = 0; i < CELLS; i++) {
      out[i] = counts[i] > 0 ? 2 : explored[i] !== 0 ? 1 : 0
    }
    return out
  }

  /** Reveal the whole map for a player. Used by the shot harness, never in play. */
  revealAll(player: number): void {
    this.explored[player].fill(1)
    this.counts[player].fill(1)
  }
}
